import axios, { AxiosResponse } from 'axios';
import {
  FiatResponse,
  CreateFiatRequest,
  CreateSoupRequest,
  CreateExchangeRequest,
  UpdateSoupRequest,
  UpdateExchangeRequest,
  UpdateFilterRequest,
  BankUpdateReportDTOUpdate,
  ApiResponse
} from '../types/api.types';

// Базовый URL API (при использовании прокси оставляем пустым)
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Интерсептор для обработки ошибок
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error);
    if (error.code === 'ECONNABORTED') {
       console.error('Превышено время ожидания ответа от сервера');
    }
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('access_token');
      window.location.reload(); // Simple way to reset state
    }
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      console.error('Сервер недоступен. Проверьте, что API запущен на', API_BASE_URL);
    }
    throw error;
  }
);

// Интерсептор для логирования запросов и добавления токена
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.baseURL}${config.url}`);
    return config;
  },
  (error) => {
    console.error('Request Error:', error);
    return Promise.reject(error);
  }
);

export class ApiService {
  // Авторизация
  static async login(password: string): Promise<void> {
    const formData = new URLSearchParams();
    formData.append('username', 'admin');
    formData.append('password', password);

    const response = await api.post('/api/token', formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });

    if (response.data && response.data.access_token) {
      localStorage.setItem('access_token', response.data.access_token);
    }
  }

  static logout(): void {
    localStorage.removeItem('access_token');
  }

  static isAuthenticated(): boolean {
    return !!localStorage.getItem('access_token');
  }

  // Получение списка используемых валют
  static async getUsedFiats(): Promise<string[]> {
    const response: AxiosResponse<{ fiats: string[] }> = await api.get('/api/admin/used_fiats');
    return response.data.fiats; // Исправлено: извлекаем массив из объекта
  }

  // Получение всех доступных валют
  static async getAllFiats(): Promise<string[]> {
    const response: AxiosResponse<{ fiats: string[] }> = await api.get('/api/admin/all_fiats');
    return response.data.fiats; // Исправлено: извлекаем массив из объекта
  }

  // Получение настроек парсера для валюты
  static async getFiatSettings(fiatName: string): Promise<FiatResponse> {
    const response: AxiosResponse<FiatResponse> = await api.get(
      `/api/admin/settings_parser/${fiatName}`
    );
    return response.data;
  }

  // Получение списка бирж для валюты
  static async getExchanges(fiatName: string): Promise<string[]> {
    const response: AxiosResponse<{ exchanges: string[] }> = await api.get(
      `/api/admin/exchanges/${fiatName}`
    );
    return response.data.exchanges;
  }

  // Получение списка банков для биржи и валюты
  static async getBanks(fiatName: string, exchangeName: string): Promise<string[]> {
    const response: AxiosResponse<{ banks: string[] }> = await api.get(
      `/api/admin/banks/${fiatName}/${exchangeName}`
    );
    // Гарантируем, что всегда возвращается массив
    return Array.isArray(response.data.banks) ? response.data.banks : [];
  }

  // Создание новой валюты
  static async createFiat(data: CreateFiatRequest): Promise<ApiResponse<any>> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await api.post(
        '/api/admin/create_fiat',
        data
      );
      return response.data;
    } catch (error) {
      console.error('Ошибка создания валюты:', error);
      throw error;
    }
  }

  // Создание нового супа для валюты
  static async createSoup(fiatName: string, data: CreateSoupRequest): Promise<ApiResponse<any>> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await api.post(
        `/api/admin/create_soup/${fiatName}`,
        data
      );
      return response.data;
    } catch (error) {
      console.error('Ошибка создания супа:', error);
      throw error;
    }
  }

  // Создание новой биржи в супе
  static async createExchange(soupId: number, data: CreateExchangeRequest): Promise<ApiResponse<any>> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await api.post(
        `/api/admin/create_exchange/${soupId}`,
        data
      );
      return response.data;
    } catch (error) {
      console.error('Ошибка создания биржи:', error);
      throw error;
    }
  }

  // Обновление супа
  static async updateSoup(soupId: number, data: UpdateSoupRequest): Promise<ApiResponse<any>> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await api.put(
        `/api/admin/update_soup/${soupId}`,
        data
      );
      return response.data;
    } catch (error) {
      console.error('Ошибка обновления супа:', error);
      throw error;
    }
  }

  // Обновление биржи
  static async updateExchange(exchangeId: number, data: UpdateExchangeRequest): Promise<ApiResponse<any>> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await api.put(
        `/api/admin/update_exchange/${exchangeId}`,
        data
      );
      return response.data;
    } catch (error) {
      console.error('Ошибка обновления биржи:', error);
      throw error;
    }
  }

  // Обновление фильтра
  static async updateFilter(filterId: number, data: UpdateFilterRequest): Promise<ApiResponse<any>> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await api.put(
        `/api/admin/update_filter/${filterId}`,
        data
      );
      return response.data;
    } catch (error) {
      console.error('Ошибка обновления фильтра:', error);
      throw error;
    }
  }

  // Удаление валюты
  static async deleteFiat(fiatName: string): Promise<ApiResponse<any>> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await api.delete(
        `/api/admin/fiats/${fiatName}`
      );
      return response.data;
    } catch (error) {
      console.error('Ошибка удаления валюты:', error);
      throw error;
    }
  }

  // Удаление супа
  static async deleteSoup(soupId: number): Promise<ApiResponse<any>> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await api.delete(
        `/api/admin/soups/${soupId}`
      );
      return response.data;
    } catch (error) {
      console.error('Ошибка удаления супа:', error);
      throw error;
    }
  }

  // Удаление биржи
  static async deleteExchange(exchangeId: number): Promise<ApiResponse<any>> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await api.delete(
        `/api/admin/exchanges/${exchangeId}`
      );
      return response.data;
    } catch (error) {
      console.error('Ошибка удаления биржи:', error);
      throw error;
    }
  }

  // Получение имен всех бирж
  static async getNameExchanges(): Promise<string[]> {
    const response: AxiosResponse<string[]> = await api.get('/api/admin/name_exchanges');
    return response.data;
  }

  // Получение даты последнего обновления биржи
  static async getExchangeDateUpdate(exchangeName: string): Promise<string | null> {
    const response: AxiosResponse<string | null> = await api.get(`/api/admin/exchange_date_update/${exchangeName}`);
    return response.data;
  }

  // Получение фиатов для конкретной биржи
  static async getFiatsFromExchangeInfo(exchangeName: string): Promise<string[]> {
    const response: AxiosResponse<string[]> = await api.get(`/api/admin/fiats_from_exchange_info/${exchangeName}`);
    return response.data;
  }

  // Предварительный просмотр обновления банков (POST)
  static async updateBankFiatPreview(exchangeName: string, fiats: string[]): Promise<BankUpdateReportDTOUpdate[]> {
    const response: AxiosResponse<BankUpdateReportDTOUpdate[]> = await api.post(
      '/api/admin/update_bank_fiat/',
      fiats,
      {
        params: { exchange_name: exchangeName },
        timeout: 300000,
      }
    );
    return response.data;
  }

  // Применение обновления банков (PUT)
  static async updateBankFiatApply(exchangeName: string, fiats: string[]): Promise<void> {
    await api.put(
      '/api/admin/update_bank_fiat/',
      fiats,
      {
        params: { exchange_name: exchangeName },
        timeout: 300000,
      }
    );
  }

  // Получение списка валют со всех веб-бирж
  static async getFiatsFromAllWebExchanges(): Promise<string[]> {
    const response: AxiosResponse<string[]> = await api.get('/api/admin/fiats_from_all_web_exchanges', { timeout: 300000 });
    return response.data;
  }

  // Добавление информации о валютах
  static async addFiatsInfo(fiats: string[]): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await api.post(
      '/api/admin/add_fiats_info',
      fiats, { timeout: 300000 }
    );
    return response.data;
  }

  // Проверка доступности API
  static async checkHealth(): Promise<boolean> {
    try {
      await api.get('/health');
      return true;
    } catch (error) {
      console.error('API недоступен:', error);
      return false;
    }
  }
}

export default ApiService;
